import { RealtimeAgent, RealtimeSession, tool, } from '@openai/agents/realtime';
import { z } from 'zod';
import { CustomOpenAIRealtimeWebRTC } from './transport';
// Logger utility
function createLogger(logLevel = 'info') {
    const levels = {
        debug: 0,
        info: 1,
        warn: 2,
        error: 3,
        none: 4,
    };
    const currentLevel = levels[logLevel];
    return {
        debug: (message, ...args) => {
            if (currentLevel <= levels.debug) {
                console.log(`[WebRTC Debug] ${message}`, ...args);
            }
        },
        info: (message, ...args) => {
            if (currentLevel <= levels.info) {
                console.log(`[WebRTC Info] ${message}`, ...args);
            }
        },
        warn: (message, ...args) => {
            if (currentLevel <= levels.warn) {
                console.warn(`[WebRTC Warn] ${message}`, ...args);
            }
        },
        error: (message, ...args) => {
            if (currentLevel <= levels.error) {
                console.error(`[WebRTC Error] ${message}`, ...args);
            }
        },
    };
}
export function createWebRTCCallAdapter(config) {
    const logger = createLogger(config.logLevel);
    let realtimeSession;
    let connected = false;
    let mediaStream;
    let audioElement;
    let manualDisconnect = false;
    let systemSpeaking = false;
    let audioEventCleanup;
    let isMuted = false;
    // Event callbacks
    let onAudioReceivedCallback;
    let onUserSpeakingCallback;
    let onUserStoppedSpeakingCallback;
    let onSystemSpeakingCallback;
    let onSystemStoppedSpeakingCallback;
    let onConnectionStatusChangedCallback;
    let onErrorCallback;
    let onTranscriptCallback;
    let onCallTranscriptCallback;
    let onEndCallback;
    let transcriptPromiseResolver;
    // Configuration values
    const connectionTimeoutMs = config.connectionTimeoutMs;
    const model = config.model;
    const voice = config.voice;
    // const instructions = config.instructions;
    // const temperature = config.temperature;
    const setupAudioElements = async () => {
        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            // Create audio element for playback
            const audio = new Audio();
            audio.autoplay = true;
            logger.debug('Audio element created - autoplay:', audio.autoplay, 'volume:', audio.volume);
            return { mediaStream: stream, audioElement: audio };
        }
        catch (error) {
            logger.error('Failed to setup audio elements:', error);
            throw new Error('Failed to access microphone or setup audio');
        }
    };
    const setupAudioEventHandlers = (audioEl, stream) => {
        logger.debug('Setting up microphone analysis only (system audio detection disabled)');
        // User speaking detection via microphone stream
        const setupMicrophoneAnalysis = () => {
            try {
                const micAudioContext = new AudioContext();
                const micSource = micAudioContext.createMediaStreamSource(stream);
                const micAnalyser = micAudioContext.createAnalyser();
                micSource.connect(micAnalyser);
                micAnalyser.fftSize = 256;
                const micBufferLength = micAnalyser.frequencyBinCount;
                const micDataArray = new Uint8Array(micBufferLength);
                let userCurrentlySpeaking = false;
                const micMonitorInterval = setInterval(() => {
                    if (micAnalyser && micDataArray) {
                        micAnalyser.getByteFrequencyData(micDataArray);
                        const average = micDataArray.reduce((a, b) => a + b) / micDataArray.length;
                        // Detect user speaking (threshold can be adjusted)
                        if (average > 10 && !userCurrentlySpeaking) {
                            logger.debug('Microphone input detected - user speaking');
                            userCurrentlySpeaking = true;
                            onUserSpeakingCallback?.();
                        }
                        else if (average <= 10 && userCurrentlySpeaking) {
                            logger.debug('Microphone input stopped - user stopped speaking');
                            userCurrentlySpeaking = false;
                            onUserStoppedSpeakingCallback?.();
                        }
                    }
                }, 100); // Check every 100ms
                // Store cleanup function
                return () => {
                    clearInterval(micMonitorInterval);
                    micAudioContext.close();
                };
            }
            catch (error) {
                logger.warn('Failed to setup microphone analysis:', error);
                return () => { };
            }
        };
        // Setup microphone analysis only
        const cleanupMic = setupMicrophoneAnalysis();
        // Return cleanup function
        return () => {
            cleanupMic();
        };
    };
    const setupSessionEventHandlers = (session) => {
        logger.debug('Setting up session event handlers...');
        session.on('audio_start', () => {
            console.log('audio_start');
        });
        session.on('audio_stopped', () => {
            console.log('audio_stopped');
        });
        // Note: System speaking detection now handled by transport events (output_audio_buffer)
        // session.on('agent_start') and session.on('agent_end') are no longer used
        // Listen for audio interruptions (documented event)
        session.on('audio_interrupted', () => {
            logger.debug('Audio interrupted');
            // When audio is interrupted, agent stops speaking
            if (systemSpeaking) {
                systemSpeaking = false;
                onSystemStoppedSpeakingCallback?.();
            }
        });
        // Listen for tool approval requests (documented event)
        session.on('tool_approval_requested', (context, agent, request) => {
            logger.debug('Tool approval requested:', request);
            // Auto-approve for now, could be made configurable
            session.approve(request.approvalItem);
        });
        // Listen for guardrail trips (documented event)
        session.on('guardrail_tripped', (event) => {
            logger.warn('Guardrail tripped:', event);
            // Could emit error or handle guardrail violations
        });
        logger.debug('Session event handlers setup complete');
    };
    const createAssistantSession = async (context) => {
        try {
            // Get user JWT token using the authenticate strategy
            // Create assistant SDK client with Authorization header
            const assistantSdk = config.client;
            // Call session create endpoint with JWT token as Authorization header
            const sessionResponse = await assistantSdk.user.sessionCreate({
                sessionCreatePayload: {
                    type: 'realtime',
                },
            }, {
                headers: {
                    Authorization: `Bearer ${context.token}`,
                },
            });
            return sessionResponse.data.token;
        }
        catch (error) {
            logger.error('Failed to create assistant session:', error);
            throw new Error('Failed to create session with assistant service');
        }
    };
    const cleanup = () => {
        // Clean up audio event handlers
        if (audioEventCleanup) {
            audioEventCleanup();
            audioEventCleanup = undefined;
        }
        // Stop media stream
        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
            mediaStream = undefined;
        }
        // Clean up audio element
        if (audioElement) {
            audioElement.pause();
            audioElement.src = '';
            audioElement = undefined;
        }
    };
    const connect = async (options) => {
        console.debug('connecting to call', options);
        // Log test mode status at the beginning
        if (config.stunTestMode) {
            logger.info('🧪 STUN TEST MODE ENABLED - Using public STUN servers only');
        }
        else {
            logger.info('🎯 Production mode - Using configured TURN servers with STUN fallback');
        }
        try {
            manualDisconnect = false;
            onConnectionStatusChangedCallback?.('connecting');
            // Setup audio elements
            const { mediaStream: stream, audioElement: audio } = await setupAudioElements();
            mediaStream = stream;
            audioElement = audio;
            // Setup audio event handlers for speaking detection
            audioEventCleanup = setupAudioEventHandlers(audio, stream);
            // Determine ICE servers based on test mode
            let iceServers;
            if (config.stunTestMode) {
                // Use public STUN servers for testing
                iceServers = [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    { urls: 'stun:stun2.l.google.com:19302' },
                ];
                logger.info('🧪 STUN TEST MODE: Using public STUN servers only');
            }
            else if (options.options?.uris && options.options.uris.length > 0) {
                // Use provided TURN credentials with STUN fallback
                iceServers = [
                    // Add public STUN servers as fallback
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    // Add configured TURN servers
                    ...options.options.uris.map((uri) => ({
                        urls: uri,
                        username: options.options.username,
                        credential: options.options.password,
                    })),
                ];
                logger.info('🎯 Using TURN servers with STUN fallback');
            }
            else {
                // Fallback to public STUN servers if no TURN credentials
                iceServers = [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    { urls: 'stun:stun2.l.google.com:19302' },
                ];
                logger.warn('⚠️ No TURN credentials provided, using STUN servers only');
            }
            // Create WebRTC transport
            const transport = new CustomOpenAIRealtimeWebRTC({
                mediaStream: stream,
                audioElement: audio,
                iceServers,
                debugIceServers: config.debugIceServers,
            });
            // Setup transport event listeners for system speaking detection
            transport.on('output_audio_buffer_started', () => {
                logger.debug('System started speaking (transport event)');
                if (!systemSpeaking) {
                    systemSpeaking = true;
                    logger.debug('Updated systemSpeaking flag to true, calling onSystemSpeakingCallback');
                    onSystemSpeakingCallback?.();
                }
                else {
                    logger.debug('systemSpeaking was already true, not calling callback');
                }
            });
            transport.on('output_audio_buffer_stopped', () => {
                logger.debug('System stopped speaking (transport event)');
                if (systemSpeaking) {
                    systemSpeaking = false;
                    logger.debug('Updated systemSpeaking flag to false, calling onSystemStoppedSpeakingCallback');
                    onSystemStoppedSpeakingCallback?.();
                }
                else {
                    logger.debug('systemSpeaking was already false, not calling callback');
                }
            });
            logger.debug('WebRTC transport created with audio element and media stream');
            const endCall = tool({
                name: 'end_call',
                description: 'End the call',
                parameters: z.object({
                    summary: z.string({
                        description: 'Detailed summary of the call that includes all the relevant information and decisions made',
                    }),
                    ending_type: z.enum(['natural', 'explicit'], {
                        description: 'Type of ending: "natural" when you decide to conclude naturally, "explicit" when user explicitly requests to end the call',
                    }),
                    transcript: z.array(z.object({
                        role: z.enum(['user', 'assistant']),
                        text: z.string(),
                    }), { description: 'The raw transcript of the call' }),
                }),
                async execute(input, context) {
                    logger.info(`End call tool executed (${input.ending_type}):`, input);
                    const isExplicitEnding = input.ending_type === 'explicit';
                    logger.debug(`Ending call - type: ${input.ending_type}, systemSpeaking: ${systemSpeaking}`);
                    // For natural endings, wait until system finishes speaking
                    if (!isExplicitEnding && systemSpeaking) {
                        logger.debug('Natural ending - waiting for system to finish speaking...');
                        // Wait for system to stop speaking with reactive checking
                        await new Promise((resolve) => {
                            let resolved = false;
                            const originalCallback = onSystemStoppedSpeakingCallback;
                            // Function to check and resolve if system stopped speaking
                            const checkAndResolve = () => {
                                if (resolved)
                                    return;
                                if (!systemSpeaking) {
                                    resolved = true;
                                    logger.debug('System stopped speaking - proceeding with call termination');
                                    // Restore original callback
                                    onSystemStoppedSpeakingCallback = originalCallback;
                                    // Clean up intervals
                                    clearInterval(checkInterval);
                                    clearTimeout(maxTimeout);
                                    setTimeout(() => {
                                        // Add a little bit of padding before ending the call
                                        resolve();
                                    }, 1000);
                                }
                            };
                            // Immediate check in case system already stopped
                            checkAndResolve();
                            if (resolved)
                                return;
                            // Set up callback listener for immediate response
                            onSystemStoppedSpeakingCallback = () => {
                                logger.debug('System stopped speaking callback triggered');
                                originalCallback?.(); // Call original callback
                                checkAndResolve();
                            };
                            // Reactive checking every 250ms for responsive detection
                            const checkInterval = setInterval(() => {
                                logger.debug('Checking if system is still speaking:', systemSpeaking);
                                checkAndResolve();
                            }, 250);
                            // Maximum timeout as absolute safety net (30 seconds)
                            const maxTimeout = setTimeout(() => {
                                if (resolved)
                                    return;
                                resolved = true;
                                logger.warn('Maximum timeout reached - forcing call termination after 30 seconds');
                                // Restore original callback and cleanup
                                onSystemStoppedSpeakingCallback = originalCallback;
                                clearInterval(checkInterval);
                                resolve();
                            }, 30000);
                        });
                        logger.debug('System finished speaking, proceeding with call termination');
                    }
                    // Now proceed with ending the call
                    if (realtimeSession) {
                        realtimeSession.mute(true);
                        // Only interrupt on explicit endings to cut audio immediately
                        if (isExplicitEnding) {
                            logger.debug('Explicit ending - interrupting audio immediately');
                            realtimeSession.interrupt();
                        }
                        else {
                            logger.debug('Natural ending - not interrupting, audio finished naturally');
                        }
                    }
                    // Stop all media stream tracks
                    if (mediaStream) {
                        mediaStream.getTracks().forEach((track) => {
                            track.stop();
                            logger.debug('Stopped media track:', track.kind);
                        });
                    }
                    // Stop and clear the audio element
                    if (audioElement) {
                        audioElement.pause();
                        audioElement.currentTime = 0;
                        audioElement.srcObject = null;
                        audioElement.src = '';
                        audioElement.load();
                        logger.debug('Audio element cleared and reset');
                    }
                    logger.info(`Call ended (${input.ending_type})`);
                    const result = {
                        summary: input.summary,
                        transcript: input.transcript,
                    };
                    if (transcriptPromiseResolver) {
                        // User explicitly called end() - resolve the promise
                        transcriptPromiseResolver(result);
                        transcriptPromiseResolver = undefined;
                        logger.debug('Resolved end() promise - user initiated');
                    }
                    else {
                        // Assistant autonomously ended call - trigger callback
                        logger.debug('Assistant ended call autonomously, triggering onEnd callback');
                        await options.onEnd(result);
                    }
                    return input;
                },
            });
            const finalInstructions = `
            ${options.instructions}
            <tool_calling>
                Guidelines for how to work with tools
                <end_call>
                    Terminates the call with two different behaviors based on ending type
                    
                    Parameters:
                    - summary: Detailed summary of the call with all relevant information and decisions
                    - ending_type: Choose between "natural" or "explicit"
                    
                    Ending Types:
                    1. "natural" - Use when YOU decide the conversation is complete
                       - The system will wait for you to finish speaking before ending the call
                       - Allows for a graceful, natural conclusion without cutting off your response
                       - Use this for normal conversation endings when you've collected all needed information
                    
                    2. "explicit" - Use when the USER explicitly asks to end the call or if the user clicked the end call button
                       - The system will immediately cut audio and end the call
                       - Use this when user says things like "end the call", "hang up", "I need to go", etc.
                       - Provides immediate response to user's request
                    
                    Guidelines:
                    
                    - IMPORTANT: Before calling the end_call tool tell the user that you will recap the meeting in the text chat and outline the next steps
                    - Choose the ending_type carefully based on who/what initiated the ending
                    - For natural endings, you can continue speaking normally - the system will wait for you to finish
                </end_call>
            </tool_calling>
            `;
            console.log('Instructions:', finalInstructions);
            // Initialize the OpenAI SDK
            const agent = new RealtimeAgent({
                name: 'Assistant',
                instructions: finalInstructions,
                tools: [endCall],
            });
            realtimeSession = new RealtimeSession(agent, {
                transport,
                model,
                config: {
                    ...config.session,
                },
            });
            // Setup event handlers
            setupSessionEventHandlers(realtimeSession);
            // Get ephemeral token from assistant session using user JWT
            const clientSecret = await createAssistantSession({
                token: options.token,
            });
            logger.info('connecting to call', options);
            await realtimeSession.connect({ apiKey: clientSecret });
            logger.info('WebRTC connection established successfully');
            // Manually trigger connected state since sessionStart event might not fire reliably
            connected = true;
            onConnectionStatusChangedCallback?.('connected');
            realtimeSession.sendMessage({
                role: 'user',
                type: 'message',
                content: [
                    {
                        type: 'input_text',
                        text: 'Lets start',
                    },
                ],
            });
        }
        catch (error) {
            logger.error('Connection failed:', error);
            onConnectionStatusChangedCallback?.('error');
            onErrorCallback?.(error instanceof Error ? error : new Error('Connection failed'));
            throw error;
        }
    };
    return {
        async connect(options) {
            return connect(options);
        },
        async disconnect() {
            logger.info('Starting disconnect...');
            manualDisconnect = true;
            connected = false;
            systemSpeaking = false; // Reset speaking state
            // The RealtimeSession doesn't have a disconnect method, so we just clean up
            if (realtimeSession) {
                logger.debug('Cleaning up realtime session...');
                realtimeSession = undefined;
            }
            cleanup();
            logger.debug('Calling status change callback with "ended"');
            onConnectionStatusChangedCallback?.('ended');
            logger.info('Disconnect complete');
        },
        async end() {
            return new Promise((resolve, reject) => {
                if (!realtimeSession) {
                    reject(new Error('Not connected'));
                    return;
                }
                // Store the resolver so the endCall tool can resolve it
                transcriptPromiseResolver = resolve;
                try {
                    realtimeSession.interrupt();
                    realtimeSession.sendMessage({
                        role: 'user',
                        type: 'message',
                        content: [
                            {
                                type: 'input_text',
                                text: 'USER REQUESTED TO END CALL - Please call the end_call tool immediately with ending_type: "explicit" to terminate this conversation.',
                            },
                        ],
                    });
                    logger.info('Requested agent to call end_call tool');
                }
                catch (error) {
                    transcriptPromiseResolver = undefined;
                    reject(error);
                }
            });
        },
        async sendAudio(audioData) {
            if (!connected || !realtimeSession) {
                throw new Error('Not connected');
            }
            // The WebRTC transport handles audio automatically through the microphone stream
            // For manual text input, the documented sendMessage method could be used:
            // realtimeSession.sendMessage({ role: 'user', content: [{ type: 'text', text: 'message' }] });
            logger.warn('Manual audio sending not needed - WebRTC transport handles audio automatically');
        },
        onAudioReceived(callback) {
            onAudioReceivedCallback = callback;
        },
        onUserSpeaking(callback) {
            onUserSpeakingCallback = callback;
        },
        onUserStoppedSpeaking(callback) {
            onUserStoppedSpeakingCallback = callback;
        },
        onSystemSpeaking(callback) {
            onSystemSpeakingCallback = callback;
        },
        onSystemStoppedSpeaking(callback) {
            onSystemStoppedSpeakingCallback = callback;
        },
        onConnectionStatusChanged(callback) {
            onConnectionStatusChangedCallback = callback;
        },
        onError(callback) {
            onErrorCallback = callback;
        },
        isConnected() {
            return connected;
        },
        mute() {
            logger.debug('Muting microphone...');
            if (realtimeSession) {
                realtimeSession.mute(true);
                isMuted = true;
                logger.info('Microphone muted');
            }
            else {
                logger.warn('Cannot mute - not connected');
            }
        },
        unmute() {
            logger.debug('Unmuting microphone...');
            if (realtimeSession) {
                realtimeSession.mute(false);
                isMuted = false;
                logger.info('Microphone unmuted');
            }
            else {
                logger.warn('Cannot unmute - not connected');
            }
        },
        interrupt() {
            logger.debug('Interrupting system speech...');
            if (realtimeSession) {
                realtimeSession.interrupt();
                logger.info('System speech interrupted');
                // Update speaking state when interrupting
                if (systemSpeaking) {
                    systemSpeaking = false;
                    onSystemStoppedSpeakingCallback?.();
                }
            }
            else {
                logger.warn('Cannot interrupt - not connected');
            }
        },
        get muted() {
            return isMuted;
        },
        destroy() {
            manualDisconnect = true;
            connected = false;
            systemSpeaking = false; // Reset speaking state
            isMuted = false; // Reset muted state
            // Clean up session without calling non-existent methods
            if (realtimeSession) {
                realtimeSession = undefined;
            }
            cleanup();
            // Clear all callbacks
            onAudioReceivedCallback = undefined;
            onUserSpeakingCallback = undefined;
            onUserStoppedSpeakingCallback = undefined;
            onSystemSpeakingCallback = undefined;
            onSystemStoppedSpeakingCallback = undefined;
            onConnectionStatusChangedCallback = undefined;
            onErrorCallback = undefined;
            onTranscriptCallback = undefined;
            onCallTranscriptCallback = undefined;
            onEndCallback = undefined;
            transcriptPromiseResolver = undefined;
        },
    };
}
//# sourceMappingURL=webRtcCallProvider.js.map